import type {SceneThumbnail} from '../../../common/api/generated/model/sceneThumbnail'
import LoadingLogo from '../LoadingLogo'

interface SceneGridProps {
  thumbnails: SceneThumbnail[]
  alt: string
}

const renderSceneContent = (thumb: SceneThumbnail | undefined, alt: string) => {
  if (!thumb || thumb.stale || !thumb.url) {
    return <div className="scene-placeholder" />
  }
  return <img src={thumb.url} alt={alt} loading="lazy" />
}

// Layout rules (per design):
// - 0 thumbs: solo placeholder
// - 1 thumb:  solo (full-width hero)
// - 2-5:      hero + (1..4) sides
// - >5:       hero + 3 sides + last slot is "+N" (overflow count)
//
// If the project has ANY stale/missing thumbnail, show a single full-frame loading
// indicator instead of N spinners — keeps the card calm while renders are pending.
const SceneGrid = ({thumbnails, alt}: SceneGridProps) => {
  const total = thumbnails.length
  const heroThumb = thumbnails[0]
  const anyPending = total === 0 || thumbnails.some((t) => t.stale || !t.url)

  if (anyPending) {
    return (
      <div className="scene-grid solo">
        <div className="scene-hero scene-hero-loading">
          <LoadingLogo size={28} />
        </div>
      </div>
    )
  }

  if (total <= 1) {
    return (
      <div className="scene-grid solo">
        <div className="scene-hero">{renderSceneContent(heroThumb, `${alt} scene 1`)}</div>
      </div>
    )
  }

  const sideSlots = 4
  const remaining = total - 1
  const showCount = Math.min(remaining, sideSlots)
  const overflow = remaining - sideSlots

  return (
    <div className="scene-grid">
      <div className="scene-hero">{renderSceneContent(heroThumb, `${alt} scene 1`)}</div>
      <div className="scene-list">
        {Array.from({length: showCount}).map((_, i) => {
          const isLastSlot = i === sideSlots - 1
          if (overflow > 0 && isLastSlot) {
            return (
              <div className="scene-thumb scene-more" key="more">
                +{overflow + 1}
              </div>
            )
          }
          const thumbIndex = i + 1
          return (
            <div className="scene-thumb" key={thumbIndex}>
              {renderSceneContent(thumbnails[thumbIndex], `${alt} scene ${thumbIndex + 1}`)}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SceneGrid
